/* @flow */

import * as React from 'react';
import { observer, inject } from 'mobx-react';
import { observable } from 'mobx';
import { translate } from 'react-i18next';

//----------------------------------------------------------------------//
// MATERIAL UI                                                          //
//----------------------------------------------------------------------//

import Paper from '@material-ui/core/Paper';

//----------------------------------------------------------------------//
//----------------------------------------------------------------------//

import PageTitle from 'components/PageTitleLegacy/PageTitle';
import PageWrapper from 'components/PageWrapper/PageWrapper';

import ActionDrawerWrapper from 'components/ActionDrawerWrapper/ActionDrawerWrapper';

// Import global components
import AdditionalInfo from 'components/AdditionalInfo/AdditionalInfo';
import AssignedTo from 'components/AssignedTo';
import StatusIndicator from 'components/StatusIndicator/StatusIndicator';
import ActionsList from 'components/ActionsList/ActionsList';
import Activity from 'components/Activity/Activity';
import Loading from 'components/Loading/Loading';

// Import action drawer components
import {
  ReassignProjectLead,
  AssignEngineers,
  RemoveEngineers,
  ExtendProject,
  EndProject,
  AssignProjectCostCenter,
  RemoveProjectCostCenter,
  ArchiveProject,
  UnarchiveProject,
} from 'views/ActionDrawer';

// Import local components
import styles from 'common-scss/DetailsPage.module.scss';

import ProjectDetails from './components/ProjectDetails';
import ProjectLocation from './components/ProjectLocation';
import AssignedToProject from './components/AssignedToProject';
import CostCenters from './components/CostCenters';
import AssignedEngineers from './components/AssignedEngineers';

import { actionsListProject } from './actionsListData';
import { generateUrl, project } from 'api/routes';

@translate()
@inject('projectDetailsStore', 'actionDrawerStore', 'navigationStore')
@observer
export default class ProjectDetailsPage extends React.Component<*> {
  @observable loading: boolean = false;

  @observable localState: Object = {
    value: 0,
  };

  componentDidMount() {
    const projectDetailsStore = this.props.projectDetailsStore;
    projectDetailsStore.loadProjectDetails(this.props.match.params.id);
    this.props.navigationStore.reset();
    this.props.navigationStore.pushPath(0, '/projects');
    this.props.navigationStore.pushPath(1, this.props.location.pathname);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const {
      projectDetailsStore,
      match,
    }: { projectDetailsStore: observable, match: Object } = this.props;

    if (projectDetailsStore.details && projectDetailsStore.details.id !== match.params.id) {
      !projectDetailsStore.loadingProjectDetails &&
        projectDetailsStore.loadProjectDetails(match.params.id);
    }
  }

  handleChange = (eventChange: any, value: number) => {
    this.localState = { value };
  };

  loadData = () => this.props.projectDetailsStore.loadProjectDetails(this.props.match.params.id);

  render() {
    const t = this.props.t;
    const actionDrawerStore = this.props.actionDrawerStore;
    const projectDetailsStore = this.props.projectDetailsStore;
    const projectDetails = projectDetailsStore.details;

    if (!projectDetails) {
      return (
        <PageWrapper>
          <Loading />
        </PageWrapper>
      );
    }

    const title = `${t('general:project')} | ${projectDetails.name}`;

    const hasEngineers = projectDetails.engineers
      ? projectDetails.engineers.toJS().length > 0
      : false;
    const hasProjectLead = projectDetails.project_lead;

    return (
      <React.Fragment>
        <PageWrapper isFlex={false}>
          <PageTitle title={title} addLink="/" />

          <div className={styles['grid-wrapper']}>
            <Paper className={styles['feature-item']}>
              <div className={styles['details-panel']}>
                <ProjectDetails {...projectDetails} />
                <ProjectLocation {...projectDetails.location} />
                <AssignedToProject {...projectDetails.assets} id={this.props.match.params.id} />
                <CostCenters details={projectDetails.cost_centres} />
                {hasEngineers && (
                  <AssignedEngineers
                    details={projectDetails.engineers}
                    projectId={projectDetails.id}
                  />
                )}
                <AdditionalInfo title="general:project" details={projectDetails} />
              </div>

              <div className={styles['actions-panel']}>
                <StatusIndicator
                  title={t('general:projects.labels.status')}
                  status={projectDetails.status}
                />
                {hasProjectLead && (
                  <AssignedTo
                    assigned={
                      projectDetails.project_lead.first_name +
                      ' ' +
                      projectDetails.project_lead.last_name
                    }
                  />
                )}
                <ActionsList data={projectDetails} actions={actionsListProject} />
              </div>
            </Paper>
            <Paper className={styles['activity-panel']}>
              {this.props.match.params.id && (
                <Activity
                  title={true}
                  url={generateUrl(project.activity, [this.props.match.params.id])}
                  refresh={this.props.actionDrawerStore.success}
                  allowFilter={true}
                />
              )}
            </Paper>
          </div>
        </PageWrapper>

        <ActionDrawerWrapper
          appendActionToURL={true}
          onActionCompleted={() => {
            /*overriding with empty fn*/
          }}
        >
          {
            {
              reassignLead: <ReassignProjectLead project={projectDetails} />,
              assignEngineer: <AssignEngineers project={projectDetails} />,
              removeEngineer: <RemoveEngineers project={projectDetails} />,
              extend: <ExtendProject project={projectDetails} />,
              end: <EndProject project={projectDetails} />,
              assignCostCenter: <AssignProjectCostCenter project={projectDetails} />,
              removeCostCenter: <RemoveProjectCostCenter project={projectDetails} />,
              archive: <ArchiveProject project={projectDetails} />,
              unarchive: <UnarchiveProject project={projectDetails} />,
            }[actionDrawerStore.currentActionDrawer]
          }
        </ActionDrawerWrapper>
      </React.Fragment>
    );
  }
}
